/* eslint-disable @typescript-eslint/no-unused-vars --
   Payment Settings and GST Settings cards are hidden (commented out in the JSX)
   but their state and handlers are kept for re-enable. Remove this directive
   when those cards are restored or deleted. */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { actionErrorMessage } from "../../../utils/network";
import { SettingsAPI } from "../../../services/settings.service";
import { SettingsCard } from "../SettingsCard";
import { KaagazyInvoiceHistory } from "../../../components/billing/KaagazyInvoiceHistory";
import { EditableCardFooter } from "../EditableCardFooter";
import { Toggle } from "../../../components/ui/Toggle";
import { BillingAPI, SubscriptionStatus, PlanInfo } from "../../../services/billing.service";
import {
    TRIAL_DURATION_DAYS,
} from "../../../constants";
import { SubscriptionActivationWatcher } from "../../../components/billing/SubscriptionActivationWatcher";
import { UpgradeSuccessModal } from "../../../components/billing/UpgradeSuccessModal";
import { useWalletBalance } from "../../../hooks/useWalletBalance";
import { BuyCreditsModal } from "../../../components/credits/BuyCreditsModal";
import { formatPaiseAsCredits, formatPlanBase, formatPlanGstLine, formatPlanTotal } from "../../../utils/billing";

// Types imported from billing.service.ts

// ── Feature label mapping ─────────────────────────────────────────────────────

const FEATURE_LABELS: Record<string, { label: string; icon: string; comingSoon?: boolean }> = {
    ai_scribe:               { label: "AI Scribe",                    icon: "🎙️" },
    cloud_storage:           { label: "Cloud Storage",                icon: "☁️" },
    whatsapp_notifications:  { label: "WhatsApp Notifications",       icon: "💬" },
    advanced_reports:        { label: "Advanced Reports & Analytics",  icon: "📊" },
    backup_to_s3:            { label: "Automatic Cloud Backup",       icon: "🗄️" },
    immunization:            { label: "Pediatric Vaccination Schedule", icon: "💉" },
    pediatric_growth_charts: { label: "Pediatric Growth Charts",      icon: "📈" },
    abdm_sync:               { label: "ABDM Health Records Sync",     icon: "🔗", comingSoon: true },
};

const TIER_COLORS: Record<string, { card: string; badge: string; btn: string; btnHover: string; ring: string }> = {
    blue:   { card: "border-blue-200",   badge: "bg-blue-100 text-blue-700",     btn: "bg-blue-600",   btnHover: "hover:bg-blue-700",   ring: "ring-blue-500" },
    purple: { card: "border-purple-200", badge: "bg-purple-100 text-purple-700", btn: "bg-purple-600", btnHover: "hover:bg-purple-700", ring: "ring-purple-500" },
    amber:  { card: "border-amber-200",  badge: "bg-amber-100 text-amber-700",   btn: "bg-amber-600",  btnHover: "hover:bg-amber-700",  ring: "ring-amber-500" },
};

// ── Component ──────────────────────────────────────────────────────────────────

export const FinanceBilling: React.FC = () => {
    const [loading, setLoading] = useState(true);

    // Subscription state
    const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
    const [plans, setPlans] = useState<PlanInfo[]>([]);
    const [subscribing, setSubscribing] = useState<string | null>(null);
    const [loadingSub, setLoadingSub] = useState(true);
    const [watcherOpen, setWatcherOpen] = useState(false);
    const [watcherPlanLabel, setWatcherPlanLabel] = useState("");
    const [successStatus, setSuccessStatus] = useState<SubscriptionStatus | null>(null);
    const [successTierLabel, setSuccessTierLabel] = useState("");

    // Notification credit wallet (WhatsApp / SMS / email sends)
    const { balancePaise, updatedAt, gateState, loading: loadingWallet, refetch: refetchWallet } = useWalletBalance();
    const [showBuyCredits, setShowBuyCredits] = useState(false);

    // Subscription lifecycle. Cancelling takes effect at period end rather than
    // immediately, so the confirm step exists to say that plainly: the common
    // fear is losing access the moment the button is pressed.
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [reactivating, setReactivating] = useState(false);

    // Existing finance settings state
    const [paymentForm, setPaymentForm] = useState({
        upi_id:                  "",
        bank_account:            "",
        auto_payment_reminders:  false,
        invoice_format:          "standard",
    });
    const [savingPayment, setSavingPayment] = useState(false);
    // Each card stays locked until the user clicks Edit; the snapshot captures
    // the form when editing starts so Cancel can restore it.
    const [editingPayment, setEditingPayment] = useState(false);
    const paymentSnapshotRef = useRef(paymentForm);

    const [gstForm, setGstForm] = useState({
        gstin:               "",
        auto_calculate_gst:  true,
        gst_rate:            0 as 0 | 5 | 12 | 18,
        hsn_code:            "",
    });
    const [savingGst, setSavingGst] = useState(false);
    const [editingGst, setEditingGst] = useState(false);
    const gstSnapshotRef = useRef(gstForm);

    // ── Fetch subscription + plans + finance settings in parallel ──────────
    const refreshBilling = useCallback(async () => {
        try {
            const [sub, pl] = await Promise.all([
                BillingAPI.getSubscription(),
                BillingAPI.getPlans(),
            ]);
            console.log("[refreshBilling] status:", sub?.status, "has_subscription:", sub?.has_subscription);
            setSubStatus(sub);
            setPlans(pl ?? []);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        Promise.allSettled([
            BillingAPI.getSubscription(),
            BillingAPI.getPlans(),
            SettingsAPI.getFinanceSettings(),
        ]).then(([subResult, plansResult, financeResult]) => {
            if (subResult.status === "fulfilled") setSubStatus(subResult.value);
            if (plansResult.status === "fulfilled") setPlans(plansResult.value ?? []);
            if (financeResult.status === "fulfilled") {
                const data = financeResult.value;
                setPaymentForm(prev => ({ ...prev, upi_id: data.upi_id }));
                setGstForm(prev => ({ ...prev, gstin: data.gstin, gst_rate: data.gst_rate }));
            }
        }).finally(() => { setLoadingSub(false); setLoading(false); });
    }, []);

    // Auto-refresh billing status when tab becomes visible or every 30s
    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === "visible") refreshBilling(); };
        document.addEventListener("visibilitychange", onVisible);
        const interval = setInterval(refreshBilling, 30_000);
        return () => { document.removeEventListener("visibilitychange", onVisible); clearInterval(interval); };
    }, [refreshBilling]);

    const handleEditPayment = () => {
        paymentSnapshotRef.current = paymentForm;
        setEditingPayment(true);
    };

    const handleCancelEditPayment = () => {
        setPaymentForm(paymentSnapshotRef.current);
        setEditingPayment(false);
    };
    // ── Subscribe handler ───────────────────────────────────────────────────
    const handleSubscribe = async (planId: string) => {
        setSubscribing(planId);
        const plan = plans.find((p) => p.plan_id === planId);
        const tierLabel = plan?.name ?? "subscription";

        try {
            const resp = await BillingAPI.subscribe(planId);
            const { subscription_id, razorpay_key_id, short_url } = resp;

            const isTrial = plan?.is_trial === true;

            if ((window as any).Razorpay) {
                const rzp = new (window as any).Razorpay({
                    key: razorpay_key_id,
                    subscription_id,
                    name: "Kaagazy EMR",
                    description: "Clinic Subscription",
                    handler: async () => {
                        if (isTrial) {
                            // Trial plans use deferred start_at -- Razorpay doesn't
                            // send subscription.authenticated webhook reliably.
                            // Confirm activation directly via our backend.
                            try {
                                const status = await BillingAPI.confirmTrial();
                                toast.success(`${tierLabel} trial activated!`);
                                setSubStatus(status);
                                await refreshBilling();
                            } catch {
                                toast.error("Trial activation pending. Please refresh the page.");
                                await refreshBilling();
                            }
                        } else {
                            setWatcherPlanLabel(tierLabel);
                            setWatcherOpen(true);
                        }
                    },
                    modal: {
                        ondismiss: () => {
                            setSubscribing(null);
                            toast.info("Payment was not completed. You can try again anytime.");
                        },
                    },
                });
                rzp.open();
            } else if (short_url) {
                // Fallback: no inline Razorpay; open hosted page in a new tab
                window.open(short_url, "_blank");
            }
        } catch (err: any) {
            const detail = err?.body?.detail ?? err?.detail;
            const errObj = typeof detail === "object" ? detail : null;
            if (errObj?.error === "already_subscribed") {
                toast.info("You already have an active subscription");
            } else if (errObj?.error === "trial_already_used") {
                toast.warning("Your clinic has already used a trial. Please subscribe to a paid plan.");
            } else {
                toast.error(typeof detail === "string" ? detail : errObj?.message || "Failed to start subscription");
            }
        } finally {
            setSubscribing(null);
        }
    };

    const handleWatcherSuccess = (status: SubscriptionStatus) => {
        setWatcherOpen(false);
        const tier = status.display_name ?? status.plan_name ?? "subscription";
        setSuccessTierLabel(tier);
        setSuccessStatus(status);
    };

    const handleSuccessClose = () => {
        setSuccessStatus(null);
        refreshBilling();
    };

    if (loading || loadingSub) {
        return <div className="text-sm text-gray-400 py-10 text-center">Loading…</div>;
    }

    const inputCls =
        "w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

    const selectCls =
        "w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer";

    // currentPlanId is set only when subscription is genuinely active (not pending/expired/halted)
    const ACTIVE_STATUSES = ["active", "authenticated", "cancelled_scheduled"];
    const hasActiveSub = subStatus?.has_subscription && ACTIVE_STATUSES.includes(subStatus.status ?? "");
    const currentPlanId = hasActiveSub ? subStatus?.plan_id : null;
    // Hide trial plans ONLY after a successful payment (active/authenticated/expired/cancelled).
    // Pending/halted/none = no successful payment yet, trials stay visible.
    const PAID_STATUSES = ["active", "authenticated", "expired", "cancelled", "cancelled_scheduled"];
    const hadAnySubscription = subStatus?.has_subscription && PAID_STATUSES.includes(subStatus.status ?? "");

    // Figma A5 status states
    const isHalted = subStatus?.has_subscription && subStatus.status === "halted";
    const isPending = subStatus?.has_subscription && subStatus.status === "pending";
    const isCancelledScheduled =
        subStatus?.has_subscription &&
        subStatus.status === "cancelled_scheduled" &&
        !!subStatus.current_period_end &&
        new Date(subStatus.current_period_end) > new Date();

    const currentPlanLabel = subStatus?.display_name ?? subStatus?.plan_name ?? "plan";
    const isActive = hasActiveSub;
    const isTrial = Boolean(hasActiveSub && (subStatus?.is_trial || subStatus?.interval === "trial"));

    const formatDate = (d?: string | null) => {
        if (!d) return "—";
        try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
        catch { return d; }
    };

    // Cancel at period end. The backend keeps the feature flags alive until
    // current_period_end, so nothing is revoked here; only auto-renewal stops.
    const handleCancelSubscription = async () => {
        setCancelling(true);
        try {
            const result = await BillingAPI.cancelSubscription();
            toast.success(
                `Auto-renewal stopped. ${currentPlanLabel} stays active until ${formatDate(result.current_period_end)}.`,
            );
            setShowCancelConfirm(false);
            await refreshBilling();
        } catch (e) {
            toast.error(actionErrorMessage(e, "Could not cancel the subscription. Please try again."));
        } finally {
            setCancelling(false);
        }
    };

    // Undo a scheduled cancellation while the period is still running.
    const handleReactivateSubscription = async () => {
        setReactivating(true);
        try {
            await BillingAPI.reactivateSubscription();
            toast.success(`Auto-renewal resumed for ${currentPlanLabel}.`);
            await refreshBilling();
        } catch (e) {
            toast.error(actionErrorMessage(e, "Could not resume the subscription. Please try again."));
        } finally {
            setReactivating(false);
        }
    };


    // Group plans by tier for display (safe — plans defaults to [])
    const safePlans = plans || [];
    const basePlans = safePlans.filter(p => p.tier === "base" && !p.is_trial && p.interval === "annual");
    const trialPlans = safePlans.filter(p => p.is_trial);

    const handleSavePayment = async () => {
        setSavingPayment(true);
        try {
            await SettingsAPI.saveFinanceSettings({
                gstin:                gstForm.gstin,
                gst_rate:             gstForm.gst_rate,
                invoice_prefix:       "INV",
                invoice_start_number: 1,
                upi_id:               paymentForm.upi_id,
                payment_methods:      { cash: true, upi: true, card: false },
            });
            setEditingPayment(false);
            toast.success("Payment settings saved");
        } catch (err) {
            toast.error(actionErrorMessage(err, "Failed to save payment settings"));
        } finally {
            setSavingPayment(false);
        }
    };

    const handleEditGst = () => {
        gstSnapshotRef.current = gstForm;
        setEditingGst(true);
    };

    const handleCancelEditGst = () => {
        setGstForm(gstSnapshotRef.current);
        setEditingGst(false);
    };

    const handleSaveGst = async () => {
        setSavingGst(true);
        try {
            await SettingsAPI.saveFinanceSettings({
                gstin:                gstForm.gstin,
                gst_rate:             gstForm.gst_rate,
                invoice_prefix:       "INV",
                invoice_start_number: 1,
                upi_id:               paymentForm.upi_id,
                payment_methods:      { cash: true, upi: true, card: false },
            });
            setEditingGst(false);
            toast.success("GST settings saved");
        } catch (err) {
            toast.error(actionErrorMessage(err, "Failed to save GST settings"));
        } finally {
            setSavingGst(false);
        }
    };

    // Locked appearance when a card is not in edit mode.
    const lockedCls =
        "w-full border border-gray-100 rounded-lg px-3 py-2 text-base sm:text-sm text-gray-700 bg-gray-50 cursor-not-allowed";
    const lockedSelectCls = lockedCls + " appearance-none";

    return (
        <>
        {watcherOpen && (
            <SubscriptionActivationWatcher
                planLabel={watcherPlanLabel}
                onSuccess={handleWatcherSuccess}
                onDismiss={() => { setWatcherOpen(false); refreshBilling(); }}
                onRetry={() => setWatcherOpen(false)}
            />
        )}
        {successStatus && (
            <UpgradeSuccessModal
                status={successStatus}
                tierLabel={successTierLabel}
                onDashboard={() => { window.location.href = "/"; }}
                onClose={handleSuccessClose}
            />
        )}
        {showBuyCredits && (
            <BuyCreditsModal
                onClose={() => setShowBuyCredits(false)}
                onPurchased={refetchWallet}
            />
        )}
        {showCancelConfirm && subStatus && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cancel-subscription-title"
                    className="w-full max-w-md rounded-2xl bg-white p-5 sm:p-6 shadow-xl"
                >
                    <h3 id="cancel-subscription-title" className="text-base font-bold text-slate-900">
                        Stop auto-renewal?
                    </h3>
                    {/* Say what does NOT happen first. The backend cancels at period
                        end, so nothing is lost today, and that is the thing people
                        most expect to go wrong when they press Cancel. */}
                    <p className="mt-2 text-sm text-slate-600">
                        {currentPlanLabel} stays active until{" "}
                        <span className="font-semibold text-slate-900">
                            {formatDate(subStatus.current_period_end)}
                        </span>
                        . Nothing is lost today, and you keep every feature until then.
                    </p>
                    <p className="mt-2 text-sm text-slate-600">
                        After that date the plan will not renew. You can resume auto-renewal
                        any time before it.
                    </p>
                    <div className="mt-5 flex flex-wrap justify-end gap-3">
                        <button
                            onClick={() => setShowCancelConfirm(false)}
                            disabled={cancelling}
                            className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                        >
                            Keep my plan
                        </button>
                        <button
                            onClick={handleCancelSubscription}
                            disabled={cancelling}
                            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {cancelling ? "Cancelling..." : "Stop auto-renewal"}
                        </button>
                    </div>
                </div>
            </div>
        )}
        <div className="space-y-5">

            {/* ── Card 0: Subscription Plans ───────────────────────────────── */}
            <SettingsCard
                title="Subscription Plans"
                subtitle="Choose the Base annual plan for your clinic"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                        <path d="M2 17l10 5 10-5"/>
                        <path d="M2 12l10 5 10-5"/>
                    </svg>
                }
                headerRight={
                    currentPlanId ? (
                        isTrial ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                Trial Active
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                Active
                            </span>
                        )
                    ) : isHalted ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            Payment failed
                        </span>
                    ) : isPending ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Payment retrying
                        </span>
                    ) : isCancelledScheduled ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">
                            Cancellation scheduled
                        </span>
                    ) : subStatus?.status === "expired" ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            Expired
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                            Free Tier
                        </span>
                    )
                }
            >
                {loadingSub ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[0, 1, 2, 3].map(i => (
                            <div key={i} className="h-52 rounded-xl bg-slate-50 animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Trial branch — active trial with Upgrade and Talk to Sales CTAs */}
                        {isTrial && subStatus && (
                            <div className="mb-4 sm:mb-5 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-4 sm:p-5">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <p className="text-sm font-bold text-slate-900">
                                                {currentPlanLabel}
                                            </p>
                                            <span className="text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2.5 py-0.5">
                                                Trial Active
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-600">
                                            {subStatus.current_period_end
                                                ? `Your free trial is active until ${formatDate(subStatus.current_period_end)}. Upgrade to the Base annual plan or talk to our sales team for custom requirements.`
                                                : "Your free trial is currently active. Upgrade to the Base annual plan or talk to our sales team for custom requirements."}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 shrink-0">
                                        {basePlans[0] && (
                                            <button
                                                onClick={() => handleSubscribe(basePlans[0].plan_id)}
                                                disabled={subscribing === basePlans[0].plan_id}
                                                className="px-4 py-2 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                            >
                                                {subscribing === basePlans[0].plan_id ? (
                                                    <span className="flex items-center justify-center gap-2">
                                                        <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                        Processing…
                                                    </span>
                                                ) : (
                                                    "Upgrade"
                                                )}
                                            </button>
                                        )}
                                        <a
                                            href="mailto:sales@swasthiq.in"
                                            className="px-4 py-2 text-xs sm:text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors inline-flex items-center justify-center"
                                        >
                                            Talk to Sales
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* [A5] Current subscription info — active paid */}
                        {isActive && !isTrial && subStatus && (
                            <div className="mb-4 sm:mb-5 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-100 p-3 sm:p-4">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">
                                            Current Plan: {currentPlanLabel}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            Period: {formatDate(subStatus.current_period_start)} – {formatDate(subStatus.current_period_end)}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
                                            {subStatus.status}
                                        </span>
                                        {/* Without this the plan cards tell a subscriber to
                                            "Cancel current plan first" with nothing to click.
                                            cancelled_scheduled counts as active, so this is
                                            suppressed once a cancellation is already booked:
                                            the panel below owns Reactivate from that point. */}
                                        {!isCancelledScheduled && (
                                            <button
                                                onClick={() => setShowCancelConfirm(true)}
                                                className="text-xs font-semibold text-slate-600 underline underline-offset-2 hover:text-slate-900 transition-colors"
                                            >
                                                Cancel subscription
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* [A5] halted — payment failed */}
                        {isHalted && subStatus && (
                            <div className="mb-4 sm:mb-5 rounded-xl bg-red-50 border border-red-200 p-4 sm:p-5">
                                <div className="flex items-center gap-2 mb-2">
                                    <p className="text-sm font-bold text-slate-900">{currentPlanLabel}</p>
                                    <span className="text-[10px] font-semibold text-white bg-red-500 rounded-full px-2 py-0.5">
                                        Payment failed
                                    </span>
                                </div>
                                <p className="text-sm text-slate-700 mb-4">
                                    Your card on file couldn't be charged. Update your payment method to restore {currentPlanLabel}.
                                </p>
                                <button
                                    onClick={() => window.open("https://dashboard.razorpay.com", "_blank", "noopener,noreferrer")}
                                    className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                                >
                                    Update payment method
                                </button>
                            </div>
                        )}

                        {/* [A5] pending — payment retrying */}
                        {isPending && subStatus && (
                            <div className="mb-4 sm:mb-5 rounded-xl bg-amber-50 border border-amber-200 p-4 sm:p-5">
                                <div className="flex items-center gap-2 mb-2">
                                    <p className="text-sm font-bold text-slate-900">{currentPlanLabel}</p>
                                    <span className="text-[10px] font-semibold text-white bg-amber-500 rounded-full px-2 py-0.5">
                                        Payment retrying
                                    </span>
                                </div>
                                <p className="text-sm text-slate-700 mb-4">
                                    Razorpay will try again soon. No action needed.
                                </p>
                                <button
                                    onClick={() => window.open("https://dashboard.razorpay.com", "_blank", "noopener,noreferrer")}
                                    className="px-4 py-2 text-sm font-semibold text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                >
                                    Manage subscription
                                </button>
                            </div>
                        )}

                        {/* [A5] cancelled_scheduled — cancellation queued */}
                        {isCancelledScheduled && subStatus && (
                            <div className="mb-4 sm:mb-5 rounded-xl bg-white border border-slate-200 p-4 sm:p-5">
                                <div className="flex items-center gap-2 mb-2">
                                    <p className="text-sm font-bold text-slate-900">{currentPlanLabel}</p>
                                    <span className="text-[10px] font-semibold text-slate-600 bg-slate-200 rounded-full px-2 py-0.5">
                                        Cancellation scheduled
                                    </span>
                                </div>
                                <p className="text-sm text-slate-700 mb-4">
                                    {currentPlanLabel} ends on {formatDate(subStatus.current_period_end)}. You'll keep premium features until then.
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    <button
                                        onClick={handleReactivateSubscription}
                                        disabled={reactivating}
                                        className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {reactivating ? "Resuming..." : "Reactivate"}
                                    </button>
                                    <button
                                        onClick={() => toast.info("Plan change coming soon")}
                                        className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                    >
                                        Change plan
                                    </button>
                                </div>
                            </div>
                        )}


                        {/* Plan cards */}
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Available Plans</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Base Plans */}
                            {basePlans.map(plan => {
                                const accent = "blue";
                                const display = {
                                    tier: "Base", cycle: plan.interval === "annual" ? "Annual" : "Monthly",
                                    badge: "Base", accent,
                                    price: plan.price_paise > 0 ? `${formatPlanBase(plan)}/${plan.interval === "annual" ? "yr" : "mo"}` : "Free",
                                    gstLine: plan.price_paise > 0 ? formatPlanGstLine(plan) : null,
                                };
                                const colors = TIER_COLORS[accent];
                                const isCurrent = currentPlanId === plan.plan_id;
                                return (
                                    <PlanCard
                                        key={plan.plan_id}
                                        plan={plan}
                                        display={display}
                                        colors={colors}
                                        isCurrent={isCurrent}
                                        isActive={!!currentPlanId}
                                        isTrial={isTrial}
                                        subscribing={subscribing}
                                        onSubscribe={handleSubscribe}
                                    />
                                );
                            })}

                            {/* Enterprise / Custom plan */}
                            <div className="relative rounded-xl border-2 border-slate-200 bg-white p-4 sm:p-5 hover:border-slate-300 hover:shadow-md transition-all flex flex-col">
                                <div className="flex items-start justify-between mb-2 sm:mb-3">
                                    <div>
                                        <div className="flex items-center gap-1.5 sm:gap-2">
                                            <h4 className="text-sm sm:text-base font-bold text-slate-900">Enterprise</h4>
                                            <span className="text-[9px] sm:text-[10px] font-semibold rounded-full px-1.5 sm:px-2 py-0.5 bg-slate-100 text-slate-700">
                                                Custom
                                            </span>
                                        </div>
                                        <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">Tailored for your clinic</p>
                                    </div>
                                    <p className="text-base sm:text-lg font-bold text-slate-900">Custom</p>
                                </div>
                                <div className="mb-3 sm:mb-4 flex-1">
                                    <p className="text-[11px] sm:text-xs text-slate-500 leading-relaxed">
                                        A plan tailored to exactly what you need, nothing more, nothing less.
                                    </p>
                                </div>
                                {currentPlanId && !isTrial ? (
                                    <button disabled className="w-full py-1.5 sm:py-2 text-[10px] sm:text-xs font-semibold text-slate-400 bg-slate-100 border border-slate-200 rounded-lg cursor-default">
                                        Cancel current plan first
                                    </button>
                                ) : (
                                    <a
                                        href="mailto:sales@swasthiq.in"
                                        className="block w-full py-1.5 sm:py-2 text-[10px] sm:text-xs font-semibold text-white text-center rounded-lg transition-colors bg-slate-700 hover:bg-slate-800"
                                    >
                                        Talk to Sales
                                    </a>
                                )}
                            </div>
                        </div>

                        {/* Trial plans:
                            - Fresh user (no subscription): show all eligible trials
                            - Active trial: show only the current trial with "Current Plan" tag
                            - After trial used/expired: hide all trials permanently */}
                        {trialPlans.length > 0 && (() => {
                            const currentTrialPlan = currentPlanId ? trialPlans.find(p => p.plan_id === currentPlanId) : null;
                            const showAllTrials = !hadAnySubscription && trialPlans.some(p => p.trial_eligible);
                            const plansToShow = currentTrialPlan ? [currentTrialPlan] : showAllTrials ? trialPlans : [];
                            if (plansToShow.length === 0) return null;
                            return (
                                <>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-5 mb-3">
                                        {currentTrialPlan ? "Current Trial" : "Trial Plans"}
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {plansToShow.map(plan => {
                                            const isCurrent = currentPlanId === plan.plan_id;
                                            const colors = TIER_COLORS.amber;
                                            const display = {
                                                tier: plan.tier === "premium" ? "Premium Trial" : "Base Trial",
                                                cycle: `${plan.trial_days ?? TRIAL_DURATION_DAYS} days`,
                                                badge: "Trial",
                                                accent: "amber",
                                                price: plan.price_paise > 0 ? formatPlanBase(plan) : "Free",
                                                gstLine: plan.price_paise > 0 ? formatPlanGstLine(plan) : null,
                                            };
                                            return (
                                                <PlanCard
                                                    key={plan.plan_id}
                                                    plan={plan}
                                                    display={display}
                                                    colors={colors}
                                                    isCurrent={isCurrent}
                                                    isActive={!!currentPlanId}
                                                    isTrial={isTrial}
                                                    subscribing={subscribing}
                                                    onSubscribe={handleSubscribe}
                                                />
                                            );
                                        })}
                                    </div>
                                </>
                            );
                        })()}
                    </>
                )}
            </SettingsCard>

            {/* ── Card 0.5: Notification Credits ──────────────────────────── */}
            <SettingsCard
                title="Notification Credits"
                subtitle="Prepaid balance used for WhatsApp & SMS sends to patients (email sends are free)"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="6" width="20" height="13" rx="2" />
                        <path d="M2 10h20" />
                        <circle cx="16" cy="14.5" r="1.5" fill="currentColor" stroke="none" />
                    </svg>
                }
                headerRight={
                    gateState === "zero" ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            Out of credits
                        </span>
                    ) : gateState === "low" ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Running low
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                            Healthy
                        </span>
                    )
                }
            >
                {loadingWallet ? (
                    <div className="h-16 rounded-xl bg-slate-50 animate-pulse" />
                ) : (
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <p className="text-2xl font-bold text-slate-900">
                                {balancePaise !== null ? formatPaiseAsCredits(balancePaise) : "—"}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {updatedAt
                                    ? `Last updated ${new Date(updatedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
                                    : "Balance not available"}
                            </p>
                        </div>
                        <button
                            onClick={() => setShowBuyCredits(true)}
                            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Buy credits
                        </button>
                    </div>
                )}
            </SettingsCard>

            {/* Kaagazy's own GST invoices to this clinic. Distinct from the
                clinic's patient invoices, which live in the billing module. */}
            <KaagazyInvoiceHistory />

            {/* ── Card 1: Payment Settings — hidden, keep code for later re-enable
            <SettingsCard
                title="Payment Settings"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                        <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                }
                footer={
                    <EditableCardFooter
                        editing={editingPayment}
                        saving={savingPayment}
                        onEdit={handleEditPayment}
                        onSave={handleSavePayment}
                        onCancel={handleCancelEditPayment}
                    />
                }
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">UPI ID</label>
                            <input
                                className={editingPayment ? inputCls : lockedCls}
                                value={paymentForm.upi_id}
                                onChange={e => setPaymentForm({ ...paymentForm, upi_id: e.target.value })}
                                placeholder="yourname@upi"
                                disabled={!editingPayment}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Bank Account</label>
                            <input
                                className={editingPayment ? inputCls : lockedCls}
                                value={paymentForm.bank_account}
                                onChange={e => setPaymentForm({ ...paymentForm, bank_account: e.target.value })}
                                placeholder="Account Number"
                                disabled={!editingPayment}
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between py-3 border-t border-gray-100">
                        <div>
                            <p className="text-sm font-medium text-gray-900">Auto Payment Reminders</p>
                            <p className="text-xs text-gray-500 mt-0.5">Send reminders for pending payments</p>
                        </div>
                        <Toggle
                            enabled={paymentForm.auto_payment_reminders}
                            onToggle={() => setPaymentForm(prev => ({ ...prev, auto_payment_reminders: !prev.auto_payment_reminders }))}
                            disabled={!editingPayment}
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Invoice Format</label>
                        <div className="relative">
                            <select
                                className={editingPayment ? selectCls : lockedSelectCls}
                                value={paymentForm.invoice_format}
                                onChange={e => setPaymentForm({ ...paymentForm, invoice_format: e.target.value })}
                                disabled={!editingPayment}
                            >
                                <option value="standard">Standard Format</option>
                                <option value="detailed">Detailed Format</option>
                                <option value="simple">Simple Format</option>
                            </select>
                            <svg className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                                width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </SettingsCard>
            */}

            {/* ── Card 2: GST Settings — hidden, keep code for later re-enable
            <SettingsCard
                title="GST Settings"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="1" x2="12" y2="23"/>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                }
                footer={
                    <EditableCardFooter
                        editing={editingGst}
                        saving={savingGst}
                        onEdit={handleEditGst}
                        onSave={handleSaveGst}
                        onCancel={handleCancelEditGst}
                    />
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">GST Number</label>
                        <input
                            className={editingGst ? inputCls : lockedCls}
                            value={gstForm.gstin}
                            onChange={e => setGstForm({ ...gstForm, gstin: e.target.value })}
                            placeholder="22AAAAA0000A1Z5"
                            maxLength={15}
                            disabled={!editingGst}
                        />
                    </div>

                    <div className="flex items-center justify-between py-3 border-t border-gray-100">
                        <div>
                            <p className="text-sm font-medium text-gray-900">Auto-calculate GST</p>
                            <p className="text-xs text-gray-500 mt-0.5">Automatically add GST to invoices</p>
                        </div>
                        <Toggle
                            enabled={gstForm.auto_calculate_gst}
                            onToggle={() => setGstForm(prev => ({ ...prev, auto_calculate_gst: !prev.auto_calculate_gst }))}
                            disabled={!editingGst}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">GST Rate</label>
                            <div className="relative">
                                <select
                                    className={editingGst ? selectCls : lockedSelectCls}
                                    value={gstForm.gst_rate}
                                    onChange={e => setGstForm({ ...gstForm, gst_rate: Number(e.target.value) as 0 | 5 | 12 | 18 })}
                                    disabled={!editingGst}
                                >
                                    <option value={0}>0%</option>
                                    <option value={5}>5%</option>
                                    <option value={12}>12%</option>
                                    <option value={18}>18%</option>
                                </select>
                                <svg className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9"/>
                                </svg>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">HSN Code</label>
                            <input
                                className={editingGst ? inputCls : lockedCls}
                                value={gstForm.hsn_code}
                                onChange={e => setGstForm({ ...gstForm, hsn_code: e.target.value })}
                                placeholder="9993"
                                disabled={!editingGst}
                            />
                        </div>
                    </div>
                </div>
            </SettingsCard>
            */}

        </div>
        </>
    );
};


// ── Plan Card Component ────────────────────────────────────────────────────────

interface PlanCardProps {
    plan: PlanInfo;
    display: { tier: string; cycle: string; badge: string; accent: string; price: string; gstLine?: string | null };
    colors: { card: string; badge: string; btn: string; btnHover: string; ring: string };
    isCurrent: boolean;
    isActive: boolean;
    isTrial?: boolean;
    subscribing: string | null;
    onSubscribe: (planId: string) => void;
    canUpgrade?: boolean;
    upgrading?: string | null;
    onUpgrade?: (planId: string) => void;
}

const PlanCard: React.FC<PlanCardProps> = ({
    plan, display, colors, isCurrent, isActive, isTrial, subscribing, onSubscribe,
    canUpgrade, upgrading, onUpgrade,
}) => {
    // Disable ONLY when there is a confirmed active subscription (Razorpay payment succeeded).
    // Never disable for pending/expired/halted - user must always be able to retry.
    // Also do not disable when on trial: trial users should be able to upgrade.
    const disabled = isActive && !isCurrent && canUpgrade !== true && !isTrial;
    return (
    <div className={`relative rounded-xl border-2 p-4 sm:p-5 transition-all flex flex-col ${
        isCurrent ? `${colors.card} ring-2 ${colors.ring} bg-white` :
        "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md"
    }`}>
        {isCurrent && (
            <div className="absolute -top-2.5 left-3 sm:left-4">
                <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-white bg-green-500 rounded-full px-2 sm:px-2.5 py-0.5">
                    Current Plan
                </span>
            </div>
        )}

        <div className="flex items-start justify-between mb-2 sm:mb-3">
            <div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                    <h4 className="text-sm sm:text-base font-bold text-slate-900">{display.tier}</h4>
                    <span className={`text-[9px] sm:text-[10px] font-semibold rounded-full px-1.5 sm:px-2 py-0.5 ${colors.badge}`}>
                        {display.badge}
                    </span>
                </div>
                <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">{display.cycle}</p>
            </div>
            <div className="text-right">
                <p className="text-base sm:text-lg font-bold text-slate-900">{display.price}</p>
                {display.gstLine && (
                    <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5 whitespace-nowrap">{display.gstLine}</p>
                )}
            </div>
        </div>

        <div className="space-y-1 sm:space-y-1.5 mb-3 sm:mb-4 flex-1">
            {plan.features.map(f => {
                const meta = FEATURE_LABELS[f] || { label: f, icon: "⚡" };
                return (
                    <div key={f} className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs text-slate-600">
                        <span className={`text-xs sm:text-sm ${meta.comingSoon ? "text-slate-400" : "text-green-500"}`}>✓</span>
                        <span className={meta.comingSoon ? "text-slate-400" : ""}>{meta.label}</span>
                        {meta.comingSoon && (
                            <span className="text-[8px] sm:text-[9px] font-semibold text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5">Soon</span>
                        )}
                    </div>
                );
            })}
        </div>

        <div className="mt-auto">
        {isCurrent ? (
            <button disabled className="w-full py-1.5 sm:py-2 text-[10px] sm:text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg cursor-default">
                ✓ Subscribed
            </button>
        ) : canUpgrade ? (
            <button
                onClick={() => onUpgrade?.(plan.plan_id)}
                disabled={upgrading === plan.plan_id}
                className={`w-full py-1.5 sm:py-2 text-[10px] sm:text-xs font-semibold text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colors.btn} ${upgrading !== plan.plan_id ? colors.btnHover : ""}`}
            >
                {upgrading === plan.plan_id ? (
                    <span className="flex items-center justify-center gap-2">
                        <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Getting quote...
                    </span>
                ) : (
                    `Upgrade - pay only the difference`
                )}
            </button>
        ) : (
            <button
                onClick={() => onSubscribe(plan.plan_id)}
                disabled={disabled}
                className={`w-full py-1.5 sm:py-2 text-[10px] sm:text-xs font-semibold text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colors.btn} ${!disabled ? colors.btnHover : ""}`}
            >
                {subscribing === plan.plan_id ? (
                    <span className="flex items-center justify-center gap-2">
                        <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing…
                    </span>
                ) : isTrial ? (
                    `Upgrade — ${display.price}`
                ) : isActive ? (
                    "Cancel current plan first"
                ) : (
                    // The headline above is the ex-GST quote, so the button carries
                    // the gross. Checkout asks for this figure, and a button that
                    // promised the quote instead would read as an 18% jump at payment.
                    `Subscribe: ${formatPlanTotal(plan)} incl. GST`
                )}
            </button>
        )}
        </div>
    </div>
    );
};
